import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, PackagePlus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const KINDS = [{v:"asset_template",l:"Equipment Template"},{v:"repair_part",l:"Repair Part"}];
const CATS = [{v:"industrial_equipment",l:"Industrial Equipment"},{v:"vehicle",l:"Vehicle"},{v:"generic",l:"Generic"}];

const emptyForm = {name:"",sku:"",kind:"asset_template",category:"industrial_equipment",manufacturer:"",model:"",unit_price:0,description:""};

export default function Assets() {
  const { user } = useAuth();
  const canEdit = ["admin","super_admin"].includes(user?.role) || user?.role === "inspector";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // null = create; object = edit
  const [form, setForm] = useState(emptyForm);
  const [fromP, setFromP] = useState(null);
  const [equipmentFields, setEquipmentFields] = useState({asset_tag:"",location:"",serial_number:"",customer_email:""});

  const load = async () => {
    const p = await api.get("/products");
    setItems(p.data);
  };
  useEffect(()=>{load();},[]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      sku: p.sku || "",
      kind: p.kind || "asset_template",
      category: p.category || "industrial_equipment",
      manufacturer: p.manufacturer || "",
      model: p.model || "",
      unit_price: Number(p.unit_price || 0),
      description: p.description || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, unit_price: Number(form.unit_price) };
      if (editing) {
        await api.patch(`/products/${editing.id}`, payload);
        toast.success("Asset updated");
      } else {
        await api.post("/products", payload);
        toast.success("Asset added");
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => { if(!confirm("Delete asset?"))return; await api.delete(`/products/${id}`); load(); };
  const createEquipment = async () => { try { await api.post(`/assets/from-product/${fromP.id}`, equipmentFields); toast.success("Equipment created from asset"); setFromP(null); setEquipmentFields({asset_tag:"",location:"",serial_number:"",customer_email:""}); load(); } catch(e){ toast.error(e.response?.data?.detail||"Failed"); } };

  return (
    <div className="p-6 md:p-8" data-testid="assets-page">
      <div className="flex items-start justify-between mb-6">
        <div><div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Catalog</div><h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Assets</h1></div>
        {canEdit && (<div className="flex gap-2">
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="new-asset-btn" onClick={openCreate} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]"><Plus className="w-4 h-4 mr-2"/>New Asset</Button></DialogTrigger>
            <DialogContent className="rounded-md" data-testid="asset-dialog">
              <DialogHeader><DialogTitle className="font-display">{editing ? `Edit Asset — ${editing.name}` : "New Asset"}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-2">
                <Field label="Name *"><Input data-testid="asset-name-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="rounded-md"/></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SKU *"><Input data-testid="asset-sku-input" value={form.sku} onChange={e=>setForm({...form,sku:e.target.value})} className="rounded-md font-mono"/></Field>
                  <Field label="Kind"><Select value={form.kind} onValueChange={v=>setForm({...form,kind:v})}><SelectTrigger className="rounded-md"><SelectValue/></SelectTrigger><SelectContent>{KINDS.map(k=><SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent></Select></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Category"><Select value={form.category} onValueChange={v=>setForm({...form,category:v})}><SelectTrigger className="rounded-md"><SelectValue/></SelectTrigger><SelectContent>{CATS.map(c=><SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent></Select></Field>
                  <Field label="Unit Price"><Input type="number" step="0.01" value={form.unit_price} onChange={e=>setForm({...form,unit_price:e.target.value})} className="rounded-md"/></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Manufacturer"><Input value={form.manufacturer} onChange={e=>setForm({...form,manufacturer:e.target.value})} className="rounded-md"/></Field>
                  <Field label="Model"><Input value={form.model} onChange={e=>setForm({...form,model:e.target.value})} className="rounded-md"/></Field>
                </div>
                <Field label="Description"><Textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="rounded-md"/></Field>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={()=>{ setOpen(false); setEditing(null); }}>Cancel</Button>
                <Button data-testid="save-asset-btn" disabled={!form.name || !form.sku} onClick={save} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">{editing ? "Save Changes" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>)}
      </div>

      {/* Assets table */}
      <div className="bg-white border border-gray-200 overflow-x-auto mb-8">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-gray-200 bg-gray-50"><Th>Name</Th><Th>SKU</Th><Th>Kind</Th><Th>Category</Th><Th>Price</Th><Th></Th></tr></thead>
          <tbody data-testid="assets-table">
            {items.length===0?<tr><td colSpan={6} className="text-center py-10 text-gray-500">No assets yet.</td></tr>:items.map(p=>(
              <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`asset-row-${p.id}`}>
                <Td className="font-semibold">{p.name}<div className="text-xs text-gray-500">{p.manufacturer} {p.model}</div></Td>
                <Td className="font-mono">{p.sku}</Td>
                <Td>{p.kind==="asset_template"?<span className="text-xs font-bold px-2 py-1 border bg-[#E0F2FE] text-[#075985] border-[#7DD3FC]">EQUIPMENT</span>:<span className="text-xs font-bold px-2 py-1 border bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]">PART</span>}</Td>
                <Td className="capitalize">{p.category?.replace("_"," ")}</Td>
                <Td className="font-mono">${Number(p.unit_price||0).toFixed(2)}</Td>
                <Td className="flex items-center gap-3">
                  {canEdit && p.kind==="asset_template" && <button data-testid={`use-asset-${p.id}`} onClick={()=>setFromP(p)} className="text-[#01ADEF] hover:text-[#0198D6]" title="Create equipment from this"><PackagePlus className="w-4 h-4"/></button>}
                  {canEdit && <button data-testid={`edit-asset-${p.id}`} onClick={()=>openEdit(p)} className="text-gray-400 hover:text-[#01ADEF]" title="Edit"><Pencil className="w-4 h-4"/></button>}
                  {user?.role==="admin" && <button data-testid={`delete-asset-${p.id}`} onClick={()=>remove(p.id)} className="text-gray-400 hover:text-[#DC2626]" title="Delete"><Trash2 className="w-4 h-4"/></button>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* From Asset dialog */}
      <Dialog open={!!fromP} onOpenChange={(o)=>!o&&setFromP(null)}>
        <DialogContent className="rounded-md">
          <DialogHeader><DialogTitle className="font-display">Create Equipment from {fromP?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Equipment Tag (auto if empty)"><Input value={equipmentFields.asset_tag} onChange={e=>setEquipmentFields({...equipmentFields,asset_tag:e.target.value})} className="rounded-md font-mono"/></Field>
            <Field label="Location *"><Input value={equipmentFields.location} onChange={e=>setEquipmentFields({...equipmentFields,location:e.target.value})} className="rounded-md"/></Field>
            <Field label="Serial Number"><Input value={equipmentFields.serial_number} onChange={e=>setEquipmentFields({...equipmentFields,serial_number:e.target.value})} className="rounded-md font-mono"/></Field>
            <Field label="Customer Email (optional)"><Input value={equipmentFields.customer_email} onChange={e=>setEquipmentFields({...equipmentFields,customer_email:e.target.value})} className="rounded-md"/></Field>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={()=>setFromP(null)}>Cancel</Button><Button data-testid="create-from-asset" onClick={createEquipment} className="rounded-full bg-[#01ADEF] hover:bg-[#0198D6]">Create Equipment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const Field = ({label,children})=>(<div><Label className="text-xs uppercase tracking-wider font-semibold text-gray-700">{label}</Label><div className="mt-1">{children}</div></div>);
const Th = ({children})=><th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({children,className=""})=><td className={`px-4 py-3 ${className}`}>{children}</td>;
